/**
 * Injects a compatibility shim into generated HTML before it's rendered in the
 * sandboxed iframe. This ensures browser globals like Image, Audio, etc. are
 * explicitly bound to `window` so they work in strict / module contexts.
 *
 * Problem: AI-generated code sometimes defines a local variable or React component
 * named `Image` (e.g. `const Image = ({src}) => <img .../>`), which shadows the
 * native `HTMLImageElement` constructor. Any subsequent `new Image()` call then
 * throws "Image is not a constructor".
 *
 * Fix: The shim runs FIRST (injected into <head>) and saves the native constructor
 * under an unambiguous alias `__PhotonImage`. We then text-replace every
 * `new Image(` in the generated source with `new __PhotonImage(` so it always
 * resolves to the real constructor regardless of what user code defines later.
 */

// Browser constructors that AI-generated code commonly calls with `new` but
// that could be shadowed by a same-named React component or variable.
// To protect a new global, just add its name here — the shim and patch are
// both derived from this single list.
export const PHOTON_GUARDED_GLOBALS = [
  'Image',               // new Image() — texture/sprite loading
  'Audio',               // new Audio() — sound effects
  'Worker',              // new Worker() — off-thread computation
  'URL',                 // new URL()    — URL parsing (very common React component name)
  'Event',               // new Event()  — custom event dispatch
  'CustomEvent',         // new CustomEvent()
  'WebSocket',           // new WebSocket() — real-time data feeds
  'XMLHttpRequest',      // new XMLHttpRequest() — legacy XHR
  'ResizeObserver',      // new ResizeObserver() — canvas resize handling
  'MutationObserver',    // new MutationObserver()
  'IntersectionObserver',// new IntersectionObserver()
  'FontFace',            // new FontFace() — dynamic font loading
  'BroadcastChannel',    // new BroadcastChannel() — cross-tab messaging
];

export function prepareHtml(html) {
  // Shim: save every guarded native constructor under a __Photon-prefixed alias
  // BEFORE any generated code runs, so shadowing can't affect them.
  const shimLines = PHOTON_GUARDED_GLOBALS
    .map(g => `  window.__Photon${g} = window.${g};`)
    .join('\n');
  const shim = `<script>\n${shimLines}\n</script>`;

  // CSS reset: force scrolling regardless of what the AI generates for html/body.
  // Without this, AI-generated pages that set overflow:hidden or height:100vh
  // will clip content inside the iframe with no way to scroll.
  // background:white is a safe baseline — the AI's own styles will override it
  // if they set a different background, but it prevents a transparent body from
  // showing the dark container behind it (especially visible in fullscreen).
  const cssReset = `<style>html,body{overflow:auto!important;min-height:100%;background:white;}</style>`;

  // Canvas resize fixup: WebGL / 2D canvases set their *drawing buffer* size via
  // the `width`/`height` attributes (NOT CSS). When the user drags the Nebula
  // splitter the iframe — and any `<canvas>` inside — gets a new CSS box, but
  // the drawing buffer stays at its original pixel size, producing stretched /
  // blurry output and wrong projection matrices.
  //
  // This shim observes every <canvas> element and keeps its drawing buffer in
  // sync with `clientWidth/Height * devicePixelRatio`. After resize it dispatches
  // a `resize` event on `window` so user code (Three.js `renderer.setSize`,
  // p5.js `windowResized`, raw WebGL `gl.viewport`) re-runs its own handler.
  // For Three.js renderers we also detect the common pattern and call
  // `setSize` + camera aspect update directly so apps that didn't bind to the
  // resize event still scale correctly.
  const canvasResizeFix = `<script>(function(){
  if (window.__photonCanvasResizeInstalled) return;
  window.__photonCanvasResizeInstalled = true;
  var resized = new WeakSet();
  function syncCanvas(c){
    var dpr = window.devicePixelRatio || 1;
    var w = Math.max(1, Math.round(c.clientWidth  * dpr));
    var h = Math.max(1, Math.round(c.clientHeight * dpr));
    if (w === c.width && h === c.height) return false;
    c.width  = w;
    c.height = h;
    return true;
  }
  function notifyResize(){
    try { window.dispatchEvent(new Event('resize')); } catch(_){}
  }
  function observeAll(){
    var canvases = document.querySelectorAll('canvas');
    canvases.forEach(function(c){
      if (resized.has(c)) return;
      resized.add(c);
      try {
        var ro = new (window.__PhotonResizeObserver || window.ResizeObserver)(function(){
          if (syncCanvas(c)) notifyResize();
        });
        ro.observe(c);
      } catch(_) {}
      // Initial sync (in case the canvas mounted at the wrong size)
      if (syncCanvas(c)) notifyResize();
    });
  }
  // Run after DOM is ready and again on a short interval to catch
  // canvases created later by user scripts.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }
  setInterval(observeAll, 1000);
})();</script>`;

  // Patch: rewrite `new X(` → `new __PhotonX(` throughout the generated source
  // so all constructor calls resolve to the saved alias, not whatever `X` is in scope.
  // Handles optional whitespace: `new Image(`, `new Image ()`, etc.
  const guardPattern = new RegExp(
    `new\\s+(${PHOTON_GUARDED_GLOBALS.join('|')})\\s*\\(`, 'g'
  );
  const patched = html.replace(guardPattern, (_, name) => `new __Photon${name}(`);

  if (patched.includes('<head>')) return patched.replace('<head>', `<head>${shim}${cssReset}${canvasResizeFix}`);
  if (patched.includes('<body>')) return patched.replace('<body>', `<body>${shim}${cssReset}${canvasResizeFix}`);
  return shim + cssReset + canvasResizeFix + patched;
}
